import { makeAutoObservable, observable, when } from 'mobx'

import type AppStore from '@src/app/AppStore'
import type { BlockNumberCommandProps } from '@src/app/command/BlockNumberCommand/BlockNumberCommand'
import type { CancelSubscriptionCommandProps } from '@src/app/command/CancelSubscriptionCommand/CancelSubscriptionCommand'
import type { ConfirmDeleteAllContactsBySourceModalProps } from '@src/app/command/ConfirmDeleteAllContactsBySourceCommand'
import type { EditBusinessHoursCommandProps } from '@src/app/command/EditBusinessHoursCommand/EditBusinessHoursCommand'
import type { RenameGroupConversationCommandProps } from '@src/app/command/RenameGroupConversationCommand'
import type { SelectPhoneNumberCommandProps } from '@src/app/command/SelectPhoneNumberCommand'
import type { TestAiAgentCommandProps } from '@src/app/command/TestAiAgentCommand/TestAiAgentCommand'
import type { AllowOveragesCommandProps } from '@src/app/command/billing/AllowOveragesCommand/AllowOveragesCommand'
import type { ChooseAutomationCreditsPlanCommandProps } from '@src/app/command/billing/ChooseAutomationCreditsPlanCommand/ChooseAutomationCreditsPlanCommand'
import type { IndividualContactDeletionCommandProps } from '@src/app/command/contact-deletion'
import type { IndividualContactSharingCommandProps } from '@src/app/command/contact-sharing/IndividualContactSharingCommand'
import type {
  SendTestRequestCommandProps,
  TestRequestDetailsCommandProps,
} from '@src/app/command/send-test-request'

import type { AddCallerIdCommandProps } from './AddCallerIdCommand'
import type { AddConsentMethodCommandProps } from './AddConsentMethodCommand/AddConsentMethodCommand'
import type { AddOrEditCallTagCommandProps } from './AddOrEditCallTagCommand/AddOrEditCallTagCommand'
import type { AddOrLinkAiKnowledgeCommandProps } from './AddOrLinkAiKnowledgeCommand/AddOrLinkAiKnowledgeCommand'
import type { AddOrLinkCustomJobsV1CommandProps } from './AddOrLinkCustomJobsV1Command/AddOrLinkCustomJobsV1Command'
import type { AddUserCommandProps } from './AddUserCommand'
import type { BusinessNameConfirmationCommandProps } from './BusinessNameConfirmationCommand'
import type { BusinessPlanFeaturesCommandProps } from './BusinessPlanFeaturesCommand/BusinessPlanFeaturesCommand'
import type { CallDestinationCommandProps } from './CallDestinationCommand'
import type { ConfirmDeleteCustomHoldTrackCommandProps } from './ConfirmDeleteCustomHoldTrackCommand/ConfirmDeleteCustomHoldTrackCommand'
import type { CreateWorkflowCommandProps } from './CreateWorkflowCommand/CreateWorkflowCommand'
import type { DeleteWorkflowStepCommandProps } from './DeleteWorkflowStepCommand/DeleteWorkflowStepCommand'
import type { DialerCommandProps } from './DialerCommand'
import type { EditCallerIdCommandProps } from './EditCallerIdCommand'
import type { EditGroupCommandProps } from './EditGroupCommand'
import type { EditSnippetCommandProps } from './EditSnippetCommand'
import type { EmailCodeCommandProps } from './EmailCodeCommand'
import type { ExportCsvCommandProps } from './ExportCsvCommand/ExportCsvCommand'
import type { ForwardAllCallsCommandProps } from './ForwardAllCallsCommand'
import type { ManageWorkflowSchedulesCommandProps } from './ManageWorkflowSchedulesCommand/ManageWorkflowSchedulesCommand'
import type { MemberStatusCommandProps } from './MemberStatusCommand/MemberStatusCommand'
import type { MutePhoneNumberCommandProps } from './MutePhoneNumberCommand'
import type { PortExistingNumberCommandProps } from './PortExistingNumberCommand'
import type { PublishInactiveWorkflowCommandProps } from './PublishInactiveWorkflowCommand'
import type { RejectWithMessageCommandProps } from './RejectWithMessageCommand'
import type { SetGreetingCommandProps } from './SetGreetingCommand'
import type { SetPasswordCommandProps } from './SetPasswordCommand'
import type { SharedWithCommandProps } from './SharedWithCommand'
import type { TollFreeCustomerProfileCommandProps } from './TollFreeCustomerProfileCommand'
import type { TransferCallActionCommandProps } from './TransferCallActionCommand/TransferCallActionCommand'
import type { UpgradePlanCommandProps } from './UpgradePlanCommand'
import type { UploadAvatarCommandProps } from './UploadAvatarCommand'
import type { UploadCommandProps } from './UploadCommand'
import type { UploadCustomTrackCommandProps } from './UploadCustomTrackCommand/UploadCustomTrackCommand'
import type { VerifyDomainCommandProps } from './VerifyDomainCommand/VerifyDomainCommand'
import type {
  PortRequestReceivedCommandProps,
  PortRequestSubmittedCommandProps,
} from './add-phone-number'
import type { ImportCsvPhoneNumbersCommandProps } from './add-phone-number/ImportCsvPhoneNumbersCommand'
import type { AutomationCreditsTierSummaryCommandProps } from './billing//AutomationCreditsTierSummaryCommand/AutomationCreditsTierSummaryCommand'
import type { BillingAutoChargeCommandProps } from './billing/AutoChargeCommand'
import type { PlanCommandProps } from './billing/PlanCommand/PlanCommand'
import type { PlanSummaryCommandProps } from './billing/PlanSummaryCommand/PlanSummaryCommand'
import type {
  EditScheduledMessageCommandProps,
  RescheduleMessageCommandProps,
  ScheduleMessageCommandProps,
} from './scheduled-message'

export type Command = (
  | { name: 'app' }
  | ({
      name: 'add or edit call tag'
    } & AddOrEditCallTagCommandProps)
  | ({ name: 'add user' } & AddUserCommandProps)
  | ({ name: 'business name confirmation' } & BusinessNameConfirmationCommandProps)
  | ({ name: 'billing auto charge' } & BillingAutoChargeCommandProps)
  | ({ name: 'billing cancel subscription' } & CancelSubscriptionCommandProps)
  | ({ name: 'billing upgrade plan' } & PlanCommandProps)
  | { name: 'billing credits' }
  | { name: 'business details help' }
  | ({ name: 'business plan features' } & BusinessPlanFeaturesCommandProps)
  | { name: 'business registration help' }
  | ({ name: 'change email code' } & EmailCodeCommandProps)
  | { name: 'change email' }
  | { name: 'change env' }
  | ({ name: 'create workflow' } & CreateWorkflowCommandProps)
  | ({ name: 'delete workflow step' } & DeleteWorkflowStepCommandProps)
  | ({ name: 'dialer' } & DialerCommandProps)
  | { name: 'do not disturb' }
  | ({ name: 'edit shared with' } & SharedWithCommandProps)
  | ({ name: 'edit snippet' } & EditSnippetCommandProps)
  | ({ name: 'forward all calls' } & ForwardAllCallsCommandProps)
  | { name: 'invite member' }
  | { name: 'add domain' }
  | ({ name: 'verify domain' } & VerifyDomainCommandProps)
  | ({ name: 'member status' } & MemberStatusCommandProps)
  | ({ name: 'mute phone number' } & MutePhoneNumberCommandProps)
  | ({ name: 'reject call with message' } & RejectWithMessageCommandProps)
  | ({ name: 'select call destination' } & CallDestinationCommandProps)
  | ({ name: 'set greeting' } & SetGreetingCommandProps)
  | ({ name: 'upload' } & UploadCommandProps)
  | ({ name: 'upload avatar' } & UploadAvatarCommandProps)
  | { name: 'referral' }
  | ({ name: 'edit group' } & EditGroupCommandProps)
  | { name: 'create webhook' }
  | ({ name: 'add or link ai knowledge' } & AddOrLinkAiKnowledgeCommandProps)
  | ({ name: 'add or link custom jobs' } & AddOrLinkCustomJobsV1CommandProps)
  | ({ name: 'send test request' } & SendTestRequestCommandProps)
  | ({ name: 'test request details' } & TestRequestDetailsCommandProps)
  | ({ name: 'schedule message' } & ScheduleMessageCommandProps)
  | ({ name: 'reschedule message' } & RescheduleMessageCommandProps)
  | ({ name: 'edit scheduled message' } & EditScheduledMessageCommandProps)
  | ({ name: 'add caller id' } & AddCallerIdCommandProps)
  | ({ name: 'edit caller id' } & EditCallerIdCommandProps)
  | { name: 'default contact sharing' }
  | { name: 'change password' }
  | ({ name: 'set password' } & SetPasswordCommandProps)
  | ({ name: 'individual contact sharing' } & IndividualContactSharingCommandProps)
  | ({ name: 'port existing number' } & PortExistingNumberCommandProps)
  | ({ name: 'port request received' } & PortRequestReceivedCommandProps)
  | ({ name: 'port request submitted' } & PortRequestSubmittedCommandProps)
  | { name: 'port request submitting' }
  | { name: 'porting drafts' }
  | ({ name: 'select phone numbers' } & SelectPhoneNumberCommandProps)
  | ({ name: 'export csv' } & ExportCsvCommandProps)
  | ({ name: 'individual contact deletion' } & IndividualContactDeletionCommandProps)
  | { name: 'delete all contacts' }
  | ({ name: 'rename group conversation' } & RenameGroupConversationCommandProps)
  | { name: 'import csv contacts' }
  | ({ name: 'import csv phone numbers' } & ImportCsvPhoneNumbersCommandProps)
  | ({ name: 'upgrade plan' } & UpgradePlanCommandProps)
  | { name: 'upgrade to scale' }
  | ({ name: 'plan summary modal' } & PlanSummaryCommandProps)
  | ({
      name: 'confirm-delete-all-contacts-by-source'
    } & ConfirmDeleteAllContactsBySourceModalProps)
  | ({ name: 'block number' } & BlockNumberCommandProps)
  | ({
      name: 'toll free registration customer profile'
    } & TollFreeCustomerProfileCommandProps)
  | { name: 'unpaid invoices' }
  | ({ name: 'add consent method' } & AddConsentMethodCommandProps)
  | ({ name: 'test ai agent' } & TestAiAgentCommandProps)
  | ({ name: 'set transfer action destination' } & TransferCallActionCommandProps)
  | ({ name: 'allow overages' } & AllowOveragesCommandProps)
  | ({ name: 'choose automation credits plan' } & ChooseAutomationCreditsPlanCommandProps)
  | ({ name: 'publish inactive workflow' } & PublishInactiveWorkflowCommandProps)
  | ({ name: 'automation credits plan' } & AutomationCreditsTierSummaryCommandProps)
  | ({ name: 'edit business hours' } & EditBusinessHoursCommandProps)
  | ({ name: 'manage workflow schedules' } & ManageWorkflowSchedulesCommandProps)
  | ({ name: 'upload custom track' } & UploadCustomTrackCommandProps)
  | ({
      name: 'confirm delete custom hold track'
    } & ConfirmDeleteCustomHoldTrackCommandProps)
) & { onClose?: () => void }

interface CommandOptions {
  centered?: boolean
  asChild?: boolean
  isDismissable?: boolean
}

export default class CommandUiStore {
  command: (Command & CommandOptions) | null = null

  constructor(private root: AppStore) {
    makeAutoObservable(this, {
      command: observable.ref,
    })
  }

  get open(): boolean {
    return Boolean(this.command)
  }

  present = (command: Command, options?: CommandOptions) => {
    this.command = { ...command, ...options }
    const onClose = this.command.onClose
    when(
      () => this.command?.name !== command.name,
      () => onClose?.(),
    )
  }

  hide = () => {
    this.command = null
  }
}
